import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import type { DeliveryJob } from '@/api/types';
import { formatLegDistance, jobLeg } from '@/lib/route';
import { toast } from '@/lib/toast';

/**
 * Says so when dispatch hands this rider a new job.
 *
 * There are no push notifications yet, so a new job used to appear by simply
 * being in the list on the next poll — noticed only by a rider who happened to
 * be looking and could tell the list had changed. For someone who is not at
 * ease with phones that is the same as not being told at all.
 *
 * Only jobs that were not on the PREVIOUS answer are announced, and never on
 * the first load: the sheet a rider opens the app to is not news. A short
 * buzz as well, on phones that allow it, because the phone is usually in a
 * pocket or a holder when this happens.
 *
 * Honest by construction: it fires off what the server just returned, so the
 * job it names is already on the sheet underneath it.
 */
export function useNewJobAlerts(jobs: readonly DeliveryJob[], ready: boolean) {
  const navigate = useNavigate();
  const seen = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!ready) return;

    const current = new Set(jobs.map((job) => job.id));
    const previous = seen.current;
    seen.current = current;
    if (!previous) return;

    const fresh = jobs.filter((job) => !previous.has(job.id) && job.status === 'ASSIGNED');
    if (fresh.length === 0) return;

    try {
      navigator.vibrate?.([120, 80, 120]);
    } catch {
      // Not every browser exposes it, and some throw without a user gesture.
    }

    if (fresh.length > 1) {
      toast.info(`${fresh.length} new jobs for you`, {
        description: 'They are under New jobs on your list.',
        action: { label: 'See them', onClick: () => navigate('/jobs') },
        duration: 8000,
      });
      return;
    }

    const job = fresh[0]!;
    const trip = formatLegDistance(jobLeg(job));
    toast.info('New job for you', {
      description: [job.pharmacyName ?? job.orderNumber, trip ? `about ${trip} trip` : null]
        .filter(Boolean)
        .join(' · '),
      action: { label: 'Open', onClick: () => navigate(`/jobs/${job.id}`) },
      // Longer than a confirmation: this asks for something, and a rider on a
      // bike may take a moment to look down.
      duration: 8000,
    });
  }, [jobs, ready, navigate]);
}
